import React, { useState } from "react"
import useMeasure from "react-use-measure"
import { useLocation } from "react-router-dom"
import {
  makeStyles,
  Popover,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Divider,
  Collapse
} from "@material-ui/core"
import { KeyboardArrowDown } from "@material-ui/icons"
import { t } from "@lingui/macro"
import dayjs from "dayjs"
import { useAuth } from "@services/useAuth"
import { Icon } from "../Icon/Icon"

const useStyles = makeStyles((theme) => ({
  popover: {
    left: ({ mobile }) => (mobile ? "24px !important" : "auto"),
    width: ({ mobile }) => (mobile ? "calc(100vw - 48px)" : 300),
    // Transform the top a bit to make it look better. Changed to use margin as transforms seem to interfere with the popover animation
    marginTop: ({ mobile }) => (mobile ? theme.spacing(-2) : null),
    marginLeft: ({ mobile }) => (mobile ? 0 : theme.spacing(1)),
    borderRadius: theme.radius(2)
  },
  backdrop: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(8, 8, 33, 0.6)",
    zIndex: 1350,
    cursor: "pointer",
    transition: "0.2s all"
  },
  menuList: {
    padding: theme.spacing(2)
  },
  menuItem: {
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    borderRadius: theme.radius(1),
    padding: theme.spacing(1, 1.5),
    gap: theme.spacing(2),
    "&:hover": {
      backgroundColor: theme.palette.action.hover
    }
  },
  menuItemIcon: {
    width: 24,
    minWidth: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  menuItemText: {
    "& .MuiTypography-root": {
      fontWeight: 500
    }
  },
  divider: {
    margin: theme.spacing(1, 0)
  },
  viewMoreButton: {
    display: "flex",
    fontWeight: 600,
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%"
  },
  viewMoreIcon: {
    transform: ({ showMore }) => (showMore ? "rotate(180deg)" : null),
    transformOrigin: "center",
    transition: "0.2s all"
  }
}))

export const NewActionMenu = ({ anchorEl, open, onClose, onItemClick, mobile, pinItems, onUploadFile }) => {
  const [showMore, setShowMore] = useState(false)
  const { permissionGroups, hasFeature, hasPermission, featureDependencies } = useAuth()
  const { pathname } = useLocation()

  // Restrictions to certain routes
  const knowledgeCategory = pathname.startsWith("/knowledge/category/")

  const today = dayjs().format("YYYY-MM-DD")
  const [popperRef] = useMeasure()

  const MENU_ITEMS = [
    {
      id: "adhoc-job",
      icon: "adhoc-dark",
      text: t`Run an ad hoc job`,
      href: `/jobs/${today}/adhoc-job/new`,
      requires: permissionGroups.createJobs
    },
    {
      id: "action",
      icon: "actions-dark",
      text: t`Raise an action`,
      href: "/hub/actions/unresolved/new",
      requires: permissionGroups.createActions
    },
    {
      id: "category-upload",
      icon: "uploads-dark",
      text: t`Upload files`,
      href: "/knowledge/category/upload",
      requires: permissionGroups.createKnowledge,
      restrictedTo: knowledgeCategory,
      mobileOnly: true
    },
    {
      id: "article",
      icon: "knowledge-dark",
      text: t`Create a new article`,
      href: "/knowledge/article/new",
      requires: permissionGroups.createKnowledge,
      desktopOnly: true
    },
    {
      id: "post",
      icon: "hub-dark",
      text: t`Create a new post`,
      href: "/hub/all/new",
      requires: permissionGroups.createPost
    },
    {
      id: "label",
      icon: "labels-dark",
      text: t`Create a new label`,
      href: "/labels/item/new",
      requires: permissionGroups.createLabels,
      feature: "labels"
    },
    {
      id: "job-template",
      icon: "templates-dark",
      text: t`Create a new job template`,
      href: "/templates/active/new/audit",
      requires: permissionGroups.createAudits
    },
    {
      id: "asset",
      icon: "assets-dark",
      text: t`Create a new asset`,
      href: "/assets/new",
      requires: permissionGroups.createAssets,
      feature: "assets"
    },
    {
      id: "supplier",
      icon: "suppliers-dark",
      text: t`Create a new supplier`,
      href: "/suppliers/new",
      requires: permissionGroups.createSuppliers,
      feature: "suppliers"
    },
    {
      id: "food-item",
      icon: "food-items-dark",
      text: t`Create a new food item`,
      href: "/food-items/new",
      requires: permissionGroups.createFoodItems,
      feature: "food"
    },
    {
      id: "prep-list",
      icon: "prep-dark",
      text: t`Create a prep list`,
      href: `/prep/list/${today}/new`,
      requires: permissionGroups.createPrep,
      feature: featureDependencies.prep
    },
    {
      id: "course",
      icon: "training-accreditation-dark",
      text: t`Create a new course`,
      href: `/training-manager/courses/new`,
      requires: permissionGroups.createTraining,
      desktopOnly: true
    },
    {
      id: "incident",
      icon: "hazards-dark",
      text: t`Report hazard or incident`,
      href: `/hazards-incidents/all-reports/new`,
      requires: permissionGroups.createIncidentHazard,
      feature: "incidents"
    },
    {
      id: "location",
      icon: "locations-dark",
      text: t`Create a new location`,
      href: `/locations/all/new`,
      requires: permissionGroups.createLocations
    },
    {
      id: "region",
      icon: "locations-dark",
      text: t`Create a new region`,
      href: `/locations/regions/new`,
      feature: "location_management",
      requires: permissionGroups.createLocations
    }
  ]

  const visiblePinnedItems = MENU_ITEMS.filter(
    (item) => pinItems?.includes(item.id) && !item.desktopOnly && item?.restrictedTo !== false
  )

  const classes = useStyles({ mobile, showMore })

  const handleMenuItemClick = (id, href) => {
    const fileUploadIds = ["category-upload"]

    if (fileUploadIds.includes(id)) {
      onUploadFile?.()
    } else {
      onItemClick?.(href)
    }

    onClose(null, "onClickMenuItem")
  }

  const toggleShowMore = (e) => {
    e.stopPropagation()
    setShowMore(!showMore)
  }

  const renderMenuItem = (item) => {
    if (item.requires && !hasPermission(item.requires)) return null
    if (item.feature && !hasFeature(item.feature)) return null

    return (
      <ListItem
        key={item.id}
        button
        onClick={() => handleMenuItemClick(item.id, item.href)}
        className={classes.menuItem}
      >
        <ListItemIcon className={classes.menuItemIcon}>
          <Icon name={item.icon} />
        </ListItemIcon>
        <ListItemText primary={t`${item.text}`} className={classes.menuItemText} />
      </ListItem>
    )
  }

  return (
    <>
      {mobile && open && (
        <Box
          role="presentation"
          className={classes.backdrop}
          onClick={onClose}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setShowMore(false)
              onClose()
            }
          }}
          tabIndex={0}
        />
      )}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={(event, reason) => {
          setShowMore(false)
          onClose(event, reason)
        }}
        anchorOrigin={{
          vertical: mobile ? "top" : "top",
          horizontal: mobile ? "center" : "right"
        }}
        transformOrigin={{
          vertical: mobile ? "bottom" : "top",
          horizontal: mobile ? "center" : "left"
        }}
        classes={{
          paper: classes.popover
        }}
        style={{ zIndex: 1350 }}
      >
        <List className={classes.menuList} ref={popperRef}>
          {mobile ? (
            <>
              {/* Main items always visible */}
              {visiblePinnedItems.map(renderMenuItem)}

              {/* Divider and View More button */}
              {visiblePinnedItems.length > 0 && <Divider className={classes.divider} />}
              <ListItem button onClick={toggleShowMore} className={classes.menuItem}>
                <ListItemText
                  primary={
                    <div className={classes.viewMoreButton}>
                      <span>{showMore ? t`View less` : t`View more`}</span>
                      <KeyboardArrowDown className={classes.viewMoreIcon} />
                    </div>
                  }
                />
              </ListItem>

              {/* Additional items shown when View More is clicked */}
              <Collapse in={showMore}>
                {MENU_ITEMS.filter(
                  (item) => !pinItems?.includes(item.id) && !item.desktopOnly && item?.restrictedTo !== false
                ).map(renderMenuItem)}
              </Collapse>
            </>
          ) : (
            // Desktop view - show all items unless they are restricted to mobile
            MENU_ITEMS.filter((item) => !item.mobileOnly).map(renderMenuItem)
          )}
        </List>
      </Popover>
    </>
  )
}
